import React, { useState, useEffect, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Table,
  Pagination,
  PaginationItem,
  PaginationLink,
  Badge,
} from "reactstrap";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import SweetAlert from "sweetalert2";
import { bulkPolicyReset, updateDiscountedRecords, clearDiscountData, saveBulkQuotationNumber, saveAddonData, clearAddonData } from "../../../../redux/bulkPolicy/action";
import convertToRupees from "../../../../utilities/convertToRupees";
import { EAI_PARTNER, SALES_MANAGER } from "../../../../constant";
import RecordDetailsModal from "./RecordDetailsModal.jsx";
import { saveDocumentAsQuotation } from "../../../../api";
import { getFromLocalStorage } from "../../../../hooks/useLocalStorage";
import { useLocation } from "react-router-dom";
const BulkDocumentSuccess = ({ validationData, handleBackClick }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [currentPage, setCurrentPage] = useState(1);
  const [discountAmount, setDiscountAmount] = useState("");
  const [selectedAssistanceAddon, setSelectedAssistanceAddon] = useState(null);
  const [selectedInsuranceAddon, setSelectedInsuranceAddon] = useState(null);
  const [selectedAssistanceAddonCost, setSelectedAssistanceAddonCost] = useState(0);
  const [selectedInsuranceAddonCost, setSelectedInsuranceAddonCost] = useState(0);
  const [totalAddonsCost, setTotalAddonsCost] = useState(0);
  const [selectedServices, setSelectedServices] = useState({});
  const [additionalCost, setAdditionalCost] = useState({});
  const [individualAddonCosts, setIndividualAddonCosts] = useState({});
  const [addonSelectedId, setAddonSelectedId] = useState({});
  const [addonInsuranceSelectedId, setAddonInsuranceSelectedId] = useState({});
  const [isSavingQuotation, setIsSavingQuotation] = useState(false);
  const itemsPerPage = 10;

  // Record Details Modal state
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [detailsModalItem, setDetailsModalItem] = useState(null);
  const [detailsModalDiscountedRecord, setDetailsModalDiscountedRecord] = useState(null);

  // Get role and user info
  const ROLE = JSON.parse(localStorage.getItem("ROLE"));
  const role_key = ROLE?.key;
  const role_id = ROLE?.role_id || '';
  const role_name = ROLE?.name || '';
  console.log("Location State documnet success:", location.state);
  const user = location.state?.user || getFromLocalStorage("user");
  console.log("Bulk Document Success - User Info:", user);
  // Get data from Redux validationData
  const reduxValidationData = useSelector((state) => state.bulkPolicy.validationData);
  const reduxDiscountData = useSelector((state) => state.bulkPolicy.discountData);
  const bulkPolicyState = useSelector((state) => state.bulkPolicy);
  const reduxAddonData = useSelector((state) => state.bulkPolicy.addonData);

  // Use Redux data if available, otherwise use props
  const dataSource = validationData || reduxValidationData;
  const {
    totalPremium = 0,
    availableBalance = 0,
    successCount = 0,
    errorCount = 0,
    successLog: rawSuccessLog = [],
    message = "",
    partnership,
    errorFile = null
  } = dataSource || {};

  // Guarantee every record has a stable unique record_index so addon keying always works.
  // useMemo prevents a new array on every render (which would cause infinite useEffect loops).
  const successLog = useMemo(
    () =>
      rawSuccessLog.map((record, idx) => ({
        ...record,
        record_index: record.record_index ?? record.id ?? record.proposer?.proposer_id ?? (idx + 1),
      })),
    [rawSuccessLog]
  );

  // Use discounted records from Redux if available
  const discountedRecords = reduxDiscountData?.discountedRecords;

  // Initialize displayRecords with latest data: discountedRecords if present, else successLog, else empty array
  const [displayRecords, setDisplayRecords] = useState(discountedRecords || successLog || []);

  // Keep displayRecords in sync when the source records change (initial load / prop change)
  useEffect(() => {
    setDisplayRecords(discountedRecords || successLog || []);
  }, [discountedRecords, successLog]);

   // Restore discountAmount from Redux if present
    useEffect(() => {
      if (reduxDiscountData && reduxDiscountData.discountedRecords && reduxDiscountData.discountedRecords.length > 0) {
        // Find the discount percentage from the first record
        const firstDiscount = reduxDiscountData.discountedRecords[0]?.discount?.percentage;
        if (firstDiscount !== undefined && firstDiscount !== null) {
          setDiscountAmount(String(firstDiscount));
        }
      }
    }, [reduxDiscountData]);

  // Restore addon state from Redux on mount (e.g., after navigating back)
  // Restore addon state from Redux on mount (e.g., after navigating back)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (
      reduxAddonData?.selectedServices &&
      Object.keys(reduxAddonData.selectedServices).length > 0
    ) {
      setSelectedServices(reduxAddonData.selectedServices);
      setAdditionalCost(reduxAddonData.additionalCost || {});
      setIndividualAddonCosts(reduxAddonData.individualAddonCosts || {});
    }
  }, []); // intentionally run once on mount only

  // Persist addon state to Redux whenever it changes so navigation doesn't lose it
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    dispatch(saveAddonData({ selectedServices, additionalCost, individualAddonCosts }));
  }, [selectedServices, additionalCost, individualAddonCosts]);
  // Pagination calculations (based on displayRecords so UI reflects addon changes)
  const totalPages = Math.ceil((displayRecords || []).length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentItems = (displayRecords || []).slice(startIndex, endIndex);

  // Calculate updated totals for both cases:
  // 1. discount applied -> use discounted final premium
  // 2. addons only -> recompute member premium using base + addon + GST
  const updatedTotalPremium = useMemo(() => {
    if (discountedRecords && Array.isArray(discountedRecords) && discountedRecords.length > 0) {
      return discountedRecords.reduce((sum, record) => sum + (record.discount?.premiumAfterDiscount || 0), 0);
    }

    if (successLog && successLog.length > 0) {
      return successLog.reduce((sum, record) => {
        const recordId = record.id || record.proposer?.proposer_id || record.record_index;
        const basePremium = Number(record.documentDetails?.base_premium || 0);
        const recordAddonCost = Number(additionalCost[recordId] || 0);
        const subtotal = basePremium + recordAddonCost;
        const gstAmount = subtotal * 0.18;
        return sum + subtotal + gstAmount;
      }, 0);
    }

    return totalPremium;
  }, [discountedRecords, successLog, additionalCost, totalPremium]);

  // Available Balance should always show the original balance
  const updatedAvailableBalance = availableBalance;

  // Balance After Amount should reflect deduction after discount/addons
  const balanceAfterAmount = availableBalance - updatedTotalPremium;

  // Calculate total addons cost whenever addon selections change
  useEffect(() => {
    const assistanceCost = selectedAssistanceAddon ? selectedAssistanceAddonCost : 0;
    const insuranceCost = selectedInsuranceAddon ? selectedInsuranceAddonCost : 0;
    const total = assistanceCost + insuranceCost;
    
    setTotalAddonsCost(total);
    
    console.log("Total Addon Costs Updated:", {
      assistanceCost,
      insuranceCost,
      total
    });
  }, [selectedAssistanceAddon, selectedAssistanceAddonCost, selectedInsuranceAddon, selectedInsuranceAddonCost]);

  // Auto-apply discount whenever discountAmount changes (debounced 600ms so it waits for the user to finish typing)
  // Also re-runs when addon costs change so the discounted total stays accurate after addon add/remove
  useEffect(() => {
    const pct = parseFloat(discountAmount);
    if (!discountAmount || isNaN(pct) || pct < 0 || pct > 100) {
      // If the field is cleared, remove any active discount
      if (!discountAmount && reduxDiscountData?.discountedRecords) {
        dispatch(clearDiscountData());
      }
      return;
    }

    const timer = setTimeout(() => {
      console.log("Auto-applying discount:", pct, "% to all records...");
      const discountedData = calculateDiscountOnRecords(successLog, pct);
      dispatch(updateDiscountedRecords(discountedData));
    }, 600);

    return () => clearTimeout(timer);
  }, [discountAmount, additionalCost]); // re-run when discount value OR addon costs change

  // Function to handle addon selection with cost calculation and validation
  const handleAddonSelect = (selectedOption, recordId, planCost, planCOICost, typeOfAddon = null) => {
    // if (!selectedOption || !recordId) return;

    console.log("Addon Selected:", selectedOption, "for Record ID:", recordId, "(type:", typeof recordId, ")");

    // Single insurance addon validation - only one insurance addon per record
    if (selectedOption.add_on_type?.toLowerCase() === "insurance") {
      const servicesForRecord = addonInsuranceSelectedId[recordId] || null;
      // Check if THIS specific insurance addon value is already selected (mirrors generate-quote logic)
      const indexToAddon = servicesForRecord && servicesForRecord.find(s => s === selectedOption.value);
      const isInsuranceAlreadySelected = indexToAddon > 0 ? true : false;

      if (isInsuranceAlreadySelected) {
        toast.error("Only one insurance addon can be selected per record.");
        return;
      }

      setAddonInsuranceSelectedId((prevState) => ({
        ...prevState,
        [recordId]: prevState[recordId]
          ? [...prevState[recordId], selectedOption.value]
          : [selectedOption.value],
      }));
    }

    // Track selected addon ID
    setAddonSelectedId((prevState) => ({
      ...prevState,
      [recordId]: selectedOption.value,
    }));

    // Calculate addon cost — mirrors generate-quote-details-new logic exactly
    let selectedAddonPercentage = selectedOption.addon_coa_value || 0;
    let addonCost = 0;

    if (selectedOption.addon_value_type?.toLowerCase() === "fixed") {
      addonCost = selectedOption.addon_coa_value || 0;
    } else {
      addonCost = (planCost * selectedAddonPercentage) / 100;
    }

    // Truncate label if too long
    const truncatedLabel =
      selectedOption.label.length > 30
        ? `${selectedOption.label.substring(0, 30)}...`
        : selectedOption.label;

    const updatedSelectedOption = {
      ...selectedOption,
      label: truncatedLabel,
    };

    // Store selected services for the record (always append — allows same addon multiple times for assistance)
    setSelectedServices((prevState) => ({
      ...prevState,
      [recordId]: prevState[recordId]
        ? [...prevState[recordId], updatedSelectedOption]
        : [updatedSelectedOption],
    }));

    // Accumulate additional cost for the record
    setAdditionalCost((prevCost) => ({
      ...prevCost,
      [recordId]: (prevCost[recordId] || 0) + addonCost,
    }));

    // Store exact computed cost per addon per record (mirrors generate-quote reference)
    // Used by removeAddon for accurate cost reversal without recalculating
    const serviceKey = `${selectedOption.value}+${selectedOption.add_on_type?.toLowerCase()}`;
    setIndividualAddonCosts((prevCosts) => ({
      ...prevCosts,
      [recordId]: {
        ...prevCosts[recordId],
        [serviceKey]: addonCost,
      },
    }));

    // Update UI cost display
    if (selectedOption.add_on_type?.toLowerCase() === "assistance") {
      setSelectedAssistanceAddonCost((prev) => prev + addonCost);
    } else {
      setSelectedInsuranceAddonCost((prev) => prev + addonCost);
    }

    console.log(`Addon Selected: ${selectedOption.label}, Cost: ${addonCost}, Type: ${selectedOption.add_on_type}, Record: ${recordId}`);
    toast.success("Add-on added successfully");
  };

  // Function to remove addon from a record
  const removeAddon = (service, recordId) => {
    const currentServices = selectedServices[recordId] || [];

    // Remove only ONE occurrence (by index) to support multiple same-addon adds
    const firstMatchIndex = currentServices.findIndex(s => s.value === service.value && s.add_on_type?.toLowerCase() === service.add_on_type?.toLowerCase());
    const updatedServices = firstMatchIndex === -1
      ? currentServices
      : currentServices.filter((_, i) => i !== firstMatchIndex);

    // Look up the exact cost stored when this addon was added (mirrors reference individualAddonCosts)
    // Falls back to recalculation from base_premium only if not found (safety net)
    const serviceKey = `${service.value}+${service.add_on_type?.toLowerCase()}`;
    const storedCost = individualAddonCosts[recordId]?.[serviceKey];
    let removedCost = 0;
    if (storedCost !== undefined) {
      removedCost = Number(storedCost) || 0;
    } else if (service.addon_value_type?.toLowerCase() === "fixed") {
      removedCost = Number(service.addon_coa_value) || 0;
    } else {
      // Fallback: recalculate — should rarely hit since individualAddonCosts stores the exact cost at add-time
      // Always use addon_coa_value (plan-level override), never service.percentage (service-level default)
      const record = successLog.find(r => (r.id || r.proposer?.proposer_id || r.record_index) === recordId);
      const basePremium = record?.documentDetails?.base_premium || 0;
      const percentage = Number(service.addon_coa_value) || 0;
      removedCost = (basePremium * percentage) / 100;
    }

    if (updatedServices.length === 0) {
      // Remove the record entry if no services left
      const newServices = { ...selectedServices };
      delete newServices[recordId];
      setSelectedServices(newServices);

      const newCosts = { ...additionalCost };
      delete newCosts[recordId];
      setAdditionalCost(newCosts);
    } else {
      setSelectedServices((prevState) => ({
        ...prevState,
        [recordId]: updatedServices,
      }));

      setAdditionalCost((prevCost) => ({
        ...prevCost,
        [recordId]: Math.max((prevCost[recordId] || 0) - removedCost, 0),
      }));
    }

    // Update UI cost display
    if (service.add_on_type?.toLowerCase() === "assistance") {
      setSelectedAssistanceAddonCost((prev) => Math.max(prev - removedCost, 0));
    } else {
      setSelectedInsuranceAddonCost((prev) => Math.max(prev - removedCost, 0));
    }

    // Clear insurance addon tracking if removed
    if (service.add_on_type?.toLowerCase() === "insurance") {
      const newInsuranceIds = { ...addonInsuranceSelectedId };
      delete newInsuranceIds[recordId];
      setAddonInsuranceSelectedId(newInsuranceIds);
    }

    toast.success("Add-on removed successfully");
    console.log(`Addon Removed: ${service.label}`);
  };

  // Function to show record details modal with addons
  const showRecordDetails = (item, recordWithDiscount) => {
    setDetailsModalItem(item);
    setDetailsModalDiscountedRecord(recordWithDiscount || null);
    setDetailsModalOpen(true);
  };

  const toggleDetailsModal = () => {
    setDetailsModalOpen(false);
    setDetailsModalItem(null);
    setDetailsModalDiscountedRecord(null);
  };

  const calculateYears = (dob, start_date) => {
    // robust parser: accept Date objects, ISO (yyyy-mm-dd) or common d-m-yyyy / d/m/yyyy
    const parseToUTCDate = (input) => {
      if (!input) return null;
      if (input instanceof Date)
        return new Date(
          Date.UTC(input.getFullYear(), input.getMonth(), input.getDate()),
        );
      const s = String(input).trim();
      // ISO yyyy-mm-dd
      let m = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
      if (m) return new Date(Date.UTC(+m[1], +m[2] - 1, +m[3]));
      // dd-mm-yyyy or dd/mm/yyyy (treat as day-first)
      m = s.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})$/);
      if (m) return new Date(Date.UTC(+m[3], +m[2] - 1, +m[1]));
      // fallback to Date parsing but normalize to UTC midnight
      const dt = new Date(s);
      if (isNaN(dt)) return null;
      return new Date(Date.UTC(dt.getFullYear(), dt.getMonth(), dt.getDate()));
    };

    const dobDate = parseToUTCDate(dob);
    const refDate = new Date();
    if (!dobDate || !refDate) return null;

    let years = refDate.getUTCFullYear() - dobDate.getUTCFullYear();
    const refMonth = refDate.getUTCMonth();
    const refDay = refDate.getUTCDate();
    const dobMonth = dobDate.getUTCMonth();
    const dobDay = dobDate.getUTCDate();

    // If birthday hasn't occurred yet in the reference year, subtract 1
    if (refMonth < dobMonth || (refMonth === dobMonth && refDay < dobDay)) {
      years--;
    }
    return years;
  };
  // Show summary popup on component mount
  useEffect(() => {
    showSummaryPopup();
  }, []);

  const showSummaryPopup = () => {
    // Determine if validation is successful or has errors
    const hasErrors = errorCount > 0;
    
    SweetAlert.fire({
      title: "Bulk Document Validation Summary",
      html: `
        <div style="text-align: left; font-family: 'Poppins', sans-serif;">
          <div style="margin-bottom: 20px;">
            ${hasErrors ? `
              <h5 style="color: #dc3545; margin-bottom: 15px;">
                <i class="fa fa-exclamation-triangle" style="margin-right: 8px;"></i>
                Validation Completed with Errors
              </h5>
              <div style="margin: 15px 0; padding: 12px; background-color: #fff3cd; border: 1px solid #ffc107; border-radius: 4px;">
                <p style="margin: 5px 0; color: #856404; font-size: 14px; font-weight: 600;">
                  ⚠️ Cannot proceed until all errors are resolved!
                </p>
                <p style="margin: 8px 0 0 0; color: #856404; font-size: 13px;">
                  You must fix all failed records and re-upload with 100% success rate.
                </p>
              </div>
            ` : `
              <h5 style="color: #28a745; margin-bottom: 15px;">
                <i class="fa fa-check-circle" style="margin-right: 8px;"></i>
                ✓ Validation Successful!
              </h5>
            `}
            <p style="margin: 10px 0;"><strong>Total Amount:</strong> ${convertToRupees(updatedTotalPremium)}</p>
            <p style="margin: 10px 0;"><strong>Available Balance:</strong> ${convertToRupees(availableBalance)}</p>
            <p style="margin: 10px 0;"><strong>Balance After Amount:</strong> ${convertToRupees(balanceAfterAmount)}</p>
            <hr />
            <p style="margin: 10px 0;"><strong style="color: #28a745;">✓ Successful Records:</strong> <span style="color: #28a745; font-size: 18px; font-weight: bold;">${successCount}</span></p>
            <p style="margin: 10px 0;"><strong style="color: #dc3545;">✗ Failed Records:</strong> <span style="color: #dc3545; font-size: 18px; font-weight: bold;">${errorCount}</span></p>
            ${errorFile && hasErrors ? `
              <div style="margin-top: 15px; padding: 10px; background-color: #fff3cd; border: 1px solid #ffc107; border-radius: 4px;">
                <p style="margin: 5px 0; color: #856404; font-size: 13px;">
                  <i class="fa fa-exclamation-triangle" style="margin-right: 5px;"></i>
                  <strong>Error File Available:</strong> Download the file to view failed records.
                </p>
                <button 
                  id="downloadErrorFileBtn" 
                  style="margin-top: 8px; padding: 6px 12px; background-color: #dc3545; color: white; border: none; border-radius: 4px; cursor: pointer; font-size: 12px;"
                  onclick="window.open('${errorFile}', '_blank')"
                >
                  <i class="fa fa-download" style="margin-right: 5px;"></i> Download Error File
                </button>
              </div>
            ` : ''}
          </div>
          <p style="font-size: 13px; color: #666; margin-top: 15px; font-style: italic;">${message}</p>
        </div>
      `,
      icon: hasErrors ? "error" : "success",
      confirmButtonText: "OK",
      confirmButtonColor: "#00239c",
      allowOutsideClick: false,
      allowEscapeKey: false,
    });
  };

  const handleConfirmSubmit = async () => {
    try {
      // TODO: Call API to finalize bulk upload
      toast.success("Bulk documents submitted successfully!");
      dispatch(bulkPolicyReset());
      // Navigate to documents list or dashboard
      // navigate("/documents/list");
    } catch (err) {
      toast.error("Error submitting documents. Please try again.");
    }
  };

  // Save bulk quotation function - saves ONE quotation containing ALL records
  const saveQuotationForBulk = async () => {
    try {
      setIsSavingQuotation(true);

      // If already loaded from an existing quotation, skip re-saving and go straight to next step
      const existingQuotationNumber = reduxValidationData?.bulkQuotationNumber;
      if (existingQuotationNumber) {
        console.log("📋 Already using existing quotation:", existingQuotationNumber, "— skipping re-save.");
        setIsSavingQuotation(false);
        navigate('/documents/bulk/proposer-details');
        return;
      }

      console.log("💾 Starting to save bulk quotation...");
      
      const bulkFileUrl = reduxValidationData?.bulkFileUrl || '';
      const recordsToSave = discountedRecords || displayRecords || successLog;
      
      if (!recordsToSave || recordsToSave.length === 0) {
        toast.error("No records to save");
        setIsSavingQuotation(false);
        return;
      }
      
      console.log("📦 Saving bulk quotation with", recordsToSave.length, "records");
      console.log("📎 Bulk file URL:", bulkFileUrl);
      
      // Build the single quotation payload with all bulk records
      const bulkQuotationPayload = {
        documentDetails: {
          plan_id: null, // Multiple plans, so null
          quotation_number: "", // Will be generated by backend
          is_bulk: true,
          bulk_url: bulkFileUrl,
          total_records: recordsToSave.length,
          total_premium: updatedTotalPremium || totalPremium,
          partnership_id: partnership?.partnership_id || null,
          created_by_role_id: role_id || null,
          created_by_role: role_name || null,
          // Add bulk flow initial fields so they can be restored when loading
          company_id: bulkPolicyState?.companyId || bulkPolicyState?.generateQuote?.company_id || null,
          type_id: bulkPolicyState?.typeId || bulkPolicyState?.generateQuote?.policyType || null,
          document_variant: bulkPolicyState?.documentVariant || bulkPolicyState?.generateQuote?.documentVariant || null,
          bulk_upload: bulkPolicyState?.generateQuote?.bulkUpload || 'yes',
        },
        bulk_records: recordsToSave.map((record, index) => {
          const proposer = record.proposer || {};
          const proposerCommonDetails = reduxValidationData?.proposerCommonDetails || {};
          const proposerUniqueNumber = proposer.proposer_unique_number || proposerCommonDetails.proposer_unique_number || '';
          const hasProposerUniqueNumber = !!String(proposerUniqueNumber).trim();
          const documentDetails = record.documentDetails || {};
          const travellers = record.travellers || [];
          const nominees = record.nominees || [];
          const planDetails = record.planDetails || {};

          // Per-record addon data — keyed by record_index (always set) as the reliable ID
          const recordId = record.id || record.proposer?.proposer_id || record.record_index;
          const recordAddonServices = selectedServices[recordId] || [];
          const recordAddonCostTotal = additionalCost[recordId] || 0;

          // Build structured addons array matching the reference payload format
          const addonsPayload = recordAddonServices.map((svc) => {
            const serviceKey = `${svc.value}+${svc.add_on_type?.toLowerCase()}`;
            const addonAmount = individualAddonCosts[recordId]?.[serviceKey] || 0;
            return {
              service_id: svc.value,
              add_on_type: svc.add_on_type?.toUpperCase() || '',
              addon_coa_value: svc.addon_coa_value || 0,
              addon_coi_value: svc.addon_coi_value || 0,
              addon_value_type: svc.addon_value_type || 'fixed',
              addon_amount: addonAmount,
            };
          });

          // Calculate addons_coi: sum of percentage-type insurance addons' COI cost
          const addonsCoi = recordAddonServices.reduce((sum, svc) => {
            const serviceKey = `${svc.value}+${svc.add_on_type?.toLowerCase()}`;
            const addonAmount = individualAddonCosts[recordId]?.[serviceKey] || 0;
            if (svc.add_on_type?.toLowerCase() === 'insurance' && svc.addon_value_type?.toLowerCase() !== 'fixed') {
              const coiPct = Number(svc.addon_coi_value) || 0;
              const planCOI = planDetails?.plan_coi || documentDetails?.plan_coi || 0;
              return sum + (planCOI * coiPct) / 100;
            }
            return sum;
          }, 0);

          const hasDiscount = !!record.discount;
          const basePremium = Number(documentDetails?.base_premium || 0);
          const finalBasePremium = hasDiscount ? Number(record.discount?.finalBaseAmount || 0) : basePremium;
          const finalAddonsPremium = hasDiscount
            ? Number(record.discount?.finalAddonsAmount || 0)
            : Number(recordAddonCostTotal || documentDetails?.addons_premium || 0);
          const finalTotalBeforeGst = hasDiscount
            ? Number(record.discount?.totalAmount || 0)
            : finalBasePremium + finalAddonsPremium;
          const finalGstAmount = hasDiscount
            ? Number(record.discount?.taxAmount || 0)
            : finalTotalBeforeGst * 0.18;
          const finalMemberPremium = hasDiscount
            ? Number(record.discount?.premiumAfterDiscount || 0)
            : finalTotalBeforeGst + finalGstAmount;

          return {
            record_index: index + 1,
            type_id: Number(record.type_id || documentDetails.type_id),
            geographical_cover_id: record.geographical_cover_id || documentDetails.geographical_cover_id,
            geographical_cover_name: documentDetails.geographical_cover_name || '',

            // Plan-level fields (matching reference payload)
            plan_id: documentDetails.plan_id || planDetails.plan_id || null,
            plan_name: planDetails.title || planDetails.plan_name || '',
            plan_cover: planDetails.plan_cover || documentDetails.member_coverage || 0,
            plan_cost: planDetails.plan_cost || documentDetails.base_premium || 0,
            plan_basic_cost: planDetails.plan_basic_cost || documentDetails.base_premium || 0,
            plan_coi: planDetails.plan_coi || documentDetails.plan_coi || 0,
            policyType: record.type_id || documentDetails.type_id || null,
            geographical_cover: documentDetails.geographical_cover_name || '',
            startDate: documentDetails.travel_start_date || '',
            endDate: documentDetails.travel_end_date || '',
            noOfDays: documentDetails.number_of_travel_days || 0,

            // Addon totals for this specific record only
            addonCost: recordAddonCostTotal,
            addons: addonsPayload,
            addons_coi: addonsCoi.toFixed(2),
            
            // Proposer details
            proposer: {
              proposer_passport_number: hasProposerUniqueNumber ? '' : (proposer.proposer_passport_number || proposerCommonDetails.proposer_passport_number || ''),
              proposer_unique_number: hasProposerUniqueNumber ? proposerUniqueNumber : (proposer.proposer_passport_number || proposerCommonDetails.proposer_passport_number || ''),
              proposer_gender: proposer.proposer_gender || 'MALE',
              proposer_dob: proposer.proposer_dob,
              proposer_title: proposer.proposer_title || 'Mr',
              proposer_first_name: proposer.proposer_first_name,
              proposer_middle_name: proposer.proposer_middle_name || '',
              proposer_last_name: proposer.proposer_last_name,
              number_of_travel_days: proposer.number_of_travel_days || documentDetails.number_of_travel_days,
              proposer_pin_code: proposer.proposer_pin_code,
              proposer_address: proposer.proposer_address,
              isCustomerInvoice: proposer.isCustomerInvoice || false,
              isGSTN: proposer.isGSTN || false,
              proposer_email: proposer.proposer_email,
              proposer_mobile: proposer.proposer_mobile,
              state: proposer.state || '',
              city: proposer.city || '',
              proposer_pan: proposer.proposer_pan || '',
              proposer_oci: proposer.proposer_oci || false,
              proposer_oci_file_path: proposer.proposer_oci_file_path || null,
              proposer_age: proposer.proposer_age || null,
              proposer_country: proposer.proposer_country || 'India',
              // GST fields
              customer_email: proposer.customer_email || '',
              customer_mobile: proposer.customer_mobile || '',
              customer_gstn: proposer.customer_gstn || '',
              customer_company_name: proposer.customer_company_name || '',
              customer_company_address: proposer.customer_company_address || '',
              customer_company_pin_code: proposer.customer_company_pin_code || '',
              customer_company_state: proposer.customer_company_state || '',
              customer_company_city: proposer.customer_company_city || '',
            },
            
            // Document details for this record
            documentDetails: {
              geo_country: documentDetails.geo_country || [],
              travel_start_date: documentDetails.travel_start_date,
              travel_end_date: documentDetails.travel_end_date,
              number_of_travel_days: documentDetails.number_of_travel_days,
              plan_id: documentDetails.plan_id || planDetails.plan_id,
              member_premium: finalMemberPremium,
              member_coverage: documentDetails.member_coverage || 0,
              quotation_number: documentDetails.quotation_number || '',
              base_premium: finalBasePremium,
              addons_premium: finalAddonsPremium,
              company_id: documentDetails.company_id,
              partnership_id: role_key === SALES_MANAGER 
                ? (partnership?.partnership_id || planDetails?.partnership_id || user?.partnerInfo?.partnership_id) 
                : (planDetails?.partnership_id || null),
              punched_by: documentDetails.punched_by || 'bulk_upload',
              punched_at: documentDetails.punched_at || new Date().toISOString(),
              from_state: documentDetails.from_state || '',
              from_city: documentDetails.from_city || '',
              to_state: documentDetails.to_state || '',
              to_city: documentDetails.to_city || '',
              // Pricing fields - Use discount calculation values if discount applied
              pricing_base_premium: hasDiscount ? Number(record.discount?.basePremium || 0) : basePremium,
              pricing_addons_premium: finalAddonsPremium,
              pricing_msc_percent: record.discount ? record.discount.finalMscPercentage : 0,
              pricing_msc_amount: record.discount ? record.discount.mscAmount : 0,
              pricing_discount_percent: record.discount ? record.discount.percentage : 0,
              pricing_discount_amount: record.discount ? record.discount.amount : 0,
              pricing_new_base_premium: finalBasePremium,
              pricing_new_addons_premium: finalAddonsPremium,
              pricing_total_premium: finalTotalBeforeGst,
              pricing_gst_percent: 18,
              pricing_gst_amount: finalGstAmount,
              pricing_member_premium: finalMemberPremium,
            },
            
            // Travellers
            travellers: travellers.map((traveller) => ({
              traveller_passport: traveller.traveller_passport || '',
              traveller_unique_number: traveller.traveller_unique_number || traveller.traveller_passport || '',
              traveller_dob: traveller.traveller_dob || '',
              title: traveller.title || 'Mr',
              firstName: traveller.firstName || '',
              middleName: traveller.middleName || '',
              lastName: traveller.lastName || '',
              gender: traveller.gender || 'MALE',
              critical_illness: traveller.critical_illness == 'No Critical Illness' ? 'None' : (traveller.critical_illness || 'None'),
              pre_existing_disease: traveller.pre_existing_disease || 'No',
              suffering_since: traveller.suffering_since || '',
              ped_type: traveller.ped_type || '',
              under_medication: traveller.under_medication || '',
              trip_duration: Number(traveller.trip_duration) || Number(documentDetails.number_of_travel_days) || 0,
              plan_id: documentDetails.plan_id || planDetails.plan_id,
              base_coi: traveller.base_coi || documentDetails.plan_coi || 0,
              addons_coi: addonsCoi > 0 ? String(addonsCoi.toFixed(2)) : (traveller.addons_coi || documentDetails.addons_coi || '0'),
              same_as_proposer: traveller.same_as_proposer || false,
              member_premium: finalMemberPremium,
              member_coverage: traveller.member_coverage || documentDetails.member_coverage || 0,
              // Use selected addons for this specific record; fall back to original traveller addons if none selected
              addons: addonsPayload.length > 0 ? addonsPayload : (traveller.addons || []),
              addons_premium: finalAddonsPremium,
              isOci: traveller.isOci || false,
              oci_file_path: traveller.oci_file_path || null,
              age: traveller.age || null,
              relation_with_proposer: traveller.relation_with_proposer || 'Self',
            })),
            
            // Nominees
            nominees: nominees.map((nominee) => ({
              nominee_dob: nominee.nominee_dob || '',
              nominee_title: nominee.title || nominee.nominee_title || '',
              nominee_firstName: nominee.firstName || nominee.nominee_firstName || '',
              nominee_middleName: nominee.middleName || nominee.nominee_middleName || '',
              nominee_lastName: nominee.lastName || nominee.nominee_lastName || '',
              nominee_relation: nominee.nominee_relation || 'Legal Heir',
              nominee_gender: nominee.gender || nominee.nominee_gender || '',
              nominee_same_as_proposer: nominee.nominee_same_as_proposer || false,
              toc_nominee: nominee.toc_nominee || false,
            })),
            
            // Include plan details for reference
            planDetails: planDetails,
            
            // Discount info if applied
            discount: record.discount || null,
          };
        })
      };
      
      console.log("📋 Bulk quotation payload:", bulkQuotationPayload);
      console.log("📊 Summary:", {
        total_records: bulkQuotationPayload.bulk_records.length,
        total_premium: bulkQuotationPayload.documentDetails.total_premium,
        bulk_url: bulkQuotationPayload.documentDetails.bulk_url,
        is_bulk: bulkQuotationPayload.documentDetails.is_bulk
      });
      
      // Call API to save the single bulk quotation
      const res = await saveDocumentAsQuotation(bulkQuotationPayload);
      
      if (res?.status === 200) {
        console.log("✅ Bulk quotation saved successfully:", res.data);
        const quotationNumber = res.data?.result || res.data;
        
        // Save quotation number to Redux for use in payment
        console.log("💾 Saving quotation number to Redux:", quotationNumber);
        dispatch(saveBulkQuotationNumber(quotationNumber));
        setIsSavingQuotation(false);
        navigate('/documents/bulk/proposer-details',{state:{user:user}});
      } else {
        throw new Error('Failed to save quotation');
      }
      
    } catch (error) {
      console.error("❌ Error saving bulk quotation:", error);
      toast.error(error?.response?.data?.error || error?.message || "Failed to save quotation. Please try again.");
      setIsSavingQuotation(false);
    }
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      window.scrollTo(0, 0);
    }
  };

  // Function to calculate discount on base premium for all records
  const calculateDiscountOnRecords = (records, discountPercentage) => {
    if (!records || records.length === 0) {
      console.warn("No records provided for discount calculation");
      return [];
    }
    console.log("Calculating discount for records:", validationData ,"with discount percentage:", discountPercentage);
    if (discountPercentage < 0 || discountPercentage > 100) {
      console.warn("Invalid discount percentage. Must be between 0 and 100");
      return records;
    }

    
    const discountedRecords = records.map((record) => {
      
      const { planDetails, documentDetails } = record;
      const partnershipType = partnership?.type || "";
      const commmisionPercent = partnership?.commission_percent || 0;
      console.log("Calculating discount for record:", record , partnershipType, commmisionPercent ,planDetails);

      // cost = raw plan cost (base_premium from validation API = same as selectedPlanDetails[planId].cost in reference)
      const cost = documentDetails?.base_premium || 0;
      
      // Get addon cost for this specific record
      const recordAddonCost = additionalCost[record.id] || additionalCost[record.proposer?.proposer_id] || additionalCost[record.record_index] || 0;
      const additionalCOACostLive = recordAddonCost;
      const gstRate = 0.18;

      // mscPct: prefer pricing_msc_percent already stored on the record by the API (set at validation time),
      // same as planMscData.msc_percentage in the reference generate-quote flow.
      // Fall back to partnership commission_percent only when not available.
      const storedMscPct = documentDetails?.pricing_msc_percent
        ? Number(documentDetails.pricing_msc_percent) / 100
        : null;
      const mscPct = storedMscPct !== null
        ? storedMscPct
        : (planDetails?.msc_percentage ? Number(planDetails?.msc_percentage) : Number(commmisionPercent)) / 100;
      console.log("Using MSC percentage:", mscPct * 100, "% (stored:", storedMscPct !== null ? storedMscPct * 100 + "%" : "N/A", ", partnership:", commmisionPercent + "%)");
      // Mirrors reference exactly:
      // commissionAmount = (cost + addons) * mscPct  for non-EAI
      // commissionAmount = (cost + addons)            for EAI
      const commissionAmount = partnershipType !== EAI_PARTNER
        ? (cost + additionalCOACostLive) * mscPct
        : (cost + additionalCOACostLive);
      const discountPct = Number(discountPercentage) || 0;
      const discountAmount = commissionAmount * (discountPct / 100);
      const finalBaseAmount = cost - discountAmount;
      const finalAddonsAmount = additionalCOACostLive;
      const totalAmount = finalBaseAmount + finalAddonsAmount;
      const mscAmount = commissionAmount - discountAmount;
      const taxAmount = totalAmount * gstRate;
      const finalPayableAmount = totalAmount + taxAmount;
      const finalMscPercentage = mscPct * (1 - discountPct / 100) * 100;


      console.log("Discount calculation details:", {
        cost,
        additionalCOACostLive,
        mscPct,
        commissionAmount,
        discountPct,
        discountAmount,
        finalBaseAmount,
        finalAddonsAmount,
        totalAmount,
        mscAmount,
        taxAmount,
        finalPayableAmount,
      });

      return {
        ...record,
        discount: {
          percentage: discountPercentage,
          amount: discountAmount,
          basePremium: cost,
          finalBaseAmount: finalBaseAmount,
          premiumAfterDiscount: finalPayableAmount,
          finalMscPercentage: finalMscPercentage,
          mscAmount: mscAmount,
          totalAmount: totalAmount,
          taxAmount: taxAmount,
          finalAddonsAmount: finalAddonsAmount,
          commissionAmount: commissionAmount,
        },
      };
    });

    return discountedRecords;
  };

  // Function to remove discount and restore original pricing
  const handleRemoveDiscount = () => {
    console.log("🗑️ Removing discount and restoring original pricing...");
    
    // Clear discount amount in UI
    setDiscountAmount("");
    
    // Clear discount data from Redux
    dispatch(clearDiscountData());
    
    // Restore original records with addon costs if any
    const restoredRecords = successLog.map((record) => {
      const recordId = record.id || record.proposer?.proposer_id;
      const recordAddonCost = additionalCost[recordId] || 0;
      const basePremium = record.documentDetails?.base_premium || 0;

      const gstRate = 0.18;
      const newTotalBeforeGST = basePremium + recordAddonCost;
      const gstAmount = newTotalBeforeGST * gstRate;
      const newMemberPremium = newTotalBeforeGST + gstAmount;

      return {
        ...record,
        addonCost: recordAddonCost,
        updatedMemberPremium: newMemberPremium,
        addonDetails: selectedServices[recordId] || [],
      };
    });
    
    setDisplayRecords(restoredRecords);
    
    console.log("✅ Discount removed, original pricing restored");
  };

  // Generate page numbers to display (max 5 pages + ellipsis)
  const getPaginationNumbers = () => {
    const maxVisible = 5;
    const pages = [];

    if (totalPages <= maxVisible) {
      // Show all pages if total is small
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Always show first page
      pages.push(1);

      // Calculate range around current page
      let start = Math.max(2, currentPage - 1);
      let end = Math.min(totalPages - 1, currentPage + 1);

      // Adjust if near the beginning
      if (currentPage <= 3) {
        end = 4;
      }

      // Adjust if near the end
      if (currentPage > totalPages - 3) {
        start = totalPages - 3;
      }

      // Add ellipsis if needed
      if (start > 2) {
        pages.push("...");
      }

      // Add range
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }

      // Add ellipsis if needed
      if (end < totalPages - 1) {
        pages.push("...");
      }

      // Always show last page
      pages.push(totalPages);
    }

    return pages;
  };

  return (
    <Row>
      {/* Summary Card */}
      <Col xl="12" sm="12" style={{ paddingLeft: "0px", marginBottom: "15px" }}>
        <Card style={{ backgroundColor: "#f8f9fa", borderLeft: `4px solid ${discountedRecords ? "#28a745" : "#00239c"}` }}>
          <CardBody style={{ padding: "12px 20px" }}>
            <Row>
              <Col md="3">
                <div style={{ textAlign: "center", padding: "10px 0" }}>
                  <div style={{ fontSize: "11px", color: "#666", marginBottom: "4px" }}>
                    Total Amount 
                  </div>
                  <div style={{ fontSize: "18px", fontWeight: "bold", color: "#00239c" }}>
                    {convertToRupees(updatedTotalPremium)}
                  </div>
                </div>
              </Col>
              <Col md="3">
                <div style={{ textAlign: "center", padding: "10px 0", borderLeft: "1px solid #ddd" }}>
                  <div style={{ fontSize: "11px", color: "#666", marginBottom: "4px" }}>
                    Available Balance
                  </div>
                  <div style={{ fontSize: "18px", fontWeight: "bold", color: "#28a745" }}>
                    {convertToRupees(availableBalance)}
                  </div>
                </div>
              </Col>
              <Col md="3">
                <div style={{ textAlign: "center", padding: "10px 0", borderLeft: "1px solid #ddd" }}>
                  <div style={{ fontSize: "11px", color: "#666", marginBottom: "4px" }}>
                    Balance After Amount
                  </div>
                  <div style={{ fontSize: "18px", fontWeight: "bold", color: balanceAfterAmount < 0 ? "#dc3545" : "#ffc107" }}>
                    {convertToRupees(balanceAfterAmount)}
                    {balanceAfterAmount < 0 && (
                      <span style={{ fontSize: "11px", display: "block", color: "#dc3545" }}>Insufficient Balance</span>
                    )}
                  </div>
                </div>
              </Col>
              <Col md="3">
                <div style={{ textAlign: "center", padding: "10px 0", borderLeft: "1px solid #ddd" }}>
                  <div style={{ fontSize: "11px", color: "#666", marginBottom: "4px" }}>
                    Successful Records
                  </div>
                  <div style={{ fontSize: "18px", fontWeight: "bold", color: "#28a745" }}>
                    {successCount}
                  </div>
                </div>
              </Col>
            </Row>
          </CardBody>
        </Card>
      </Col>

      {/* Error File Download Section - Only show if errorFile exists */}
      {errorFile && errorCount > 0 ? (
        <Col xl="12" sm="12" style={{ paddingLeft: "0px", marginBottom: "15px" }}>
          <Card style={{ backgroundColor: "#fff3cd", borderLeft: "4px solid #ffc107" }}>
            <CardBody style={{ padding: "12px 20px" }}>
              <Row className="align-items-center">
                <Col md="9">
                  <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                    <i className="fa fa-exclamation-triangle" style={{ fontSize: "24px", color: "#856404" }}></i>
                    <div>
                      <h6 style={{ margin: "0", fontSize: "14px", color: "#856404", fontWeight: "600" }}>
                        <strong>{errorCount}</strong> Record{errorCount > 1 ? 's' : ''} Failed Validation
                      </h6>
                      <small style={{ fontSize: "12px", color: "#856404" }}>
                        Download the error file to view details of failed records and resolve issues.
                      </small>
                    </div>
                  </div>
                </Col>
                <Col md="3" className="text-right">
                  <Button
                    color="danger"
                    size="sm"
                    onClick={() => {
                      console.log("🔽 Downloading error file:", errorFile);
                      const link = document.createElement('a');
                      link.href = errorFile;
                      link.download = `bulk_errors_${new Date().getTime()}.csv`;
                      link.target = "_blank";
                      document.body.appendChild(link);
                      link.click();
                      document.body.removeChild(link);
                      toast.success("Error file download started");
                    }}
                    style={{ padding: "6px 16px", fontSize: "12px", whiteSpace: "nowrap" }}
                  >
                    <i className="fa fa-download" style={{ marginRight: "6px" }}></i>
                    Download Error File
                  </Button>
                </Col>
              </Row>
            </CardBody>
          </Card>
        </Col>
      ) : null}

      {/* Data Table */}
      <Col xl="12" sm="12" style={{ paddingLeft: "0px", marginBottom: "15px" }}>
        <Card>
          <CardBody style={{ padding: "12px 15px" }}>
            <h6 className="mb-2" style={{ fontSize: "13px" }}>
              <b>Validated Records ({successCount})</b>
            </h6>
            <div style={{ overflowX: "auto" }}>
              <Table hover responsive striped style={{ marginBottom: "0" }}>
                <thead style={{ backgroundColor: "#f8f9fa" }}>
                  <tr style={{ fontSize: "12px" }}>
                    <th style={{ width: "5%", padding: "6px 4px" }}>Sr.</th>
                    <th style={{ width: "15%", padding: "6px 4px" }}>Traveller Name</th>
                    <th style={{ width: "12%", padding: "6px 4px" }}>Passport</th>
                    <th style={{ width: "10%", padding: "6px 4px" }}>DOB</th>
                    <th style={{ width: "12%", padding: "6px 4px" }}>Travel Dates</th>
                    <th style={{ width: "12%", padding: "6px 4px" }}>Amount</th>
                    <th style={{ width: "12%", padding: "6px 4px" }}>Coverage</th>
                    <th style={{ width: "12%", padding: "6px 4px" }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {currentItems.map((item, index) => {
                    const docDetails = item.documentDetails;
                    const travellers = item.travellers || [];
                    
                    // Get first traveller details to display (or show count if multiple)
                    const firstTraveller = travellers[0] || {};
                    const travellerName = travellers.length > 0 
                      ? `${firstTraveller?.title || ""} ${firstTraveller?.firstName || ""} ${firstTraveller?.lastName || ""}`.trim()
                      : "No Traveller";
                    const travellerPassport = firstTraveller?.traveller_passport || "N/A";
                    const travellerDOB = firstTraveller?.traveller_dob 
                      ? new Date(firstTraveller.traveller_dob).toLocaleDateString() 
                      : "N/A";
                    
                    const travelDates = `${new Date(docDetails?.travel_start_date).toLocaleDateString()} - ${new Date(docDetails?.travel_end_date).toLocaleDateString()}`;

                    // Find corresponding discounted record by absolute index
                    const absoluteIndex = startIndex + index;
                    const discountedRecord = discountedRecords ? discountedRecords[absoluteIndex] : null;
                    const hasDiscount = discountedRecord && discountedRecord.discount;
                    
                    // Get addon cost for this record
                    const recordId = item.id || item.proposer?.proposer_id || item.record_index;
                    const recordAddonCost = additionalCost[recordId] || 0;
                    
                    // Calculate display values
                    let displayBasePremium, displayMemberPremium, displayAddonCost = 0;
                    
                    if (hasDiscount) {
                      // Use discounted values from Redux
                      displayBasePremium = discountedRecord.discount.finalBaseAmount;
                      displayMemberPremium = discountedRecord.discount.premiumAfterDiscount;
                      displayAddonCost = discountedRecord.discount.finalAddonsAmount;
                    } else {
                      // Use original values with addon costs added
                      displayBasePremium = docDetails?.base_premium || 0;
                      displayAddonCost = recordAddonCost;
                      const basePlusAddon = displayBasePremium + displayAddonCost;
                      const gstAmount = basePlusAddon * 0.18;
                      displayMemberPremium = basePlusAddon + gstAmount;
                    }

                    return (
                      <tr key={index} style={{ fontSize: "12px", backgroundColor: hasDiscount ? "#f0f8f0" : (displayAddonCost > 0 ? "#f0f4ff" : "transparent") }}>
                        <td style={{ padding: "6px 4px" }}>{startIndex + index + 1}</td>
                        <td style={{ padding: "6px 4px" }}>
                          <strong style={{ fontSize: "12px" }}>{travellerName}</strong>
                          {travellers.length > 1 && (
                            <>
                              <br />
                              <small style={{ color: "#666", fontSize: "10px" }}>
                                +{travellers.length - 1} more
                              </small>
                            </>
                          )}
                        </td>
                        <td style={{ padding: "6px 4px", fontSize: "11px" }}>{travellerPassport}</td>
                        <td style={{ padding: "6px 4px", fontSize: "11px" }}>{travellerDOB}</td>
                        <td style={{ padding: "6px 4px" }}>
                          <small style={{ fontSize: "10px" }}>{travelDates}</small>
                          <br />
                          <Badge color="info" style={{ fontSize: "9px", padding: "2px 6px" }}>{docDetails?.number_of_travel_days} Days</Badge>
                        </td>
                        <td style={{ padding: "6px 4px" }}>
                          <strong style={{ color: "#00239c", fontSize: "12px" }}>
                            {convertToRupees(displayMemberPremium)}
                          </strong>
                          <br />
                          <small style={{ color: "#28a745", fontSize: "10px" }}>
                            Base + GST
                          </small>
                          {displayAddonCost > 0 && (
                            <>
                              <br />
                              <small style={{ color: "#d9534f", fontSize: "10px", fontWeight: "500" }}>
                                + Addon: {convertToRupees(displayAddonCost)}
                              </small>
                            </>
                          )}
                          {hasDiscount && <br />}
                          {hasDiscount && <Badge color="success" style={{ fontSize: "8px", padding: "1px 4px", marginTop: "2px" }}>✓ Discounted</Badge>}
                        </td>
                        <td style={{ padding: "6px 4px" }}>
                          <span style={{ fontSize: "12px" }}>${docDetails?.member_coverage}</span>
                        </td>
                        <td style={{ padding: "6px 4px", textAlign: "start" }}>
                          <Button
                            size="sm"
                            color="info"
                            outline
                            onClick={() => {
                              showRecordDetails(item, discountedRecord);
                            }}
                            style={{ borderRadius: "4px", padding: "3px 4px", fontSize: "11px" }}
                          >
                            View
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div style={{ marginTop: "20px", display: "flex", justifyContent: "center", alignItems: "center", flexWrap: "wrap", gap: "6px", marginLeft: "10%" }}>
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  style={{
                    padding: "6px 12px",
                    fontSize: "11px",
                    backgroundColor: currentPage === 1 ? "#e9ecef" : "#00239c",
                    color: currentPage === 1 ? "#999" : "#fff",
                    border: "1px solid #00239c",
                    borderRadius: "4px",
                    cursor: currentPage === 1 ? "not-allowed" : "pointer",
                    fontWeight: "500",
                  }}
                >
                  ← Prev
                </button>

                <div style={{ display: "flex", gap: "3px", alignItems: "center" }}>
                  {getPaginationNumbers().map((page, idx) => (
                    page === "..." ? (
                      <span
                        key={`ellipsis-${idx}`}
                        style={{
                          padding: "4px 8px",
                          color: "#00239c",
                          fontSize: "11px",
                          fontWeight: "bold",
                        }}
                      >
                        ...
                      </span>
                    ) : (
                      <button
                        key={page}
                        onClick={() => handlePageChange(page)}
                        style={{
                          padding: "6px 10px",
                          fontSize: "11px",
                          minWidth: "32px",
                          backgroundColor: currentPage === page ? "#00239c" : "#f8f9fa",
                          color: currentPage === page ? "#fff" : "#00239c",
                          border: currentPage === page ? "1px solid #00239c" : "1px solid #00239c",
                          borderRadius: "4px",
                          cursor: "pointer",
                          fontWeight: currentPage === page ? "600" : "400",
                          transition: "all 0.3s ease",
                          textAlign: "center",
                        }}
                      >
                        {page}
                      </button>
                    )
                  ))}
                </div>

                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  style={{
                    padding: "6px 12px",
                    fontSize: "11px",
                    backgroundColor: currentPage === totalPages ? "#e9ecef" : "#00239c",
                    color: currentPage === totalPages ? "#999" : "#fff",
                    border: "1px solid #00239c",
                    borderRadius: "4px",
                    cursor: currentPage === totalPages ? "not-allowed" : "pointer",
                    fontWeight: "500",
                  }}
                >
                  Next →
                </button>

                <span style={{ fontSize: "10px", color: "#00239c", marginLeft: "12px", fontWeight: "500" }}>
                  Page <strong>{currentPage}</strong> of <strong>{totalPages}</strong>
                </span>
              </div>
            )}
          </CardBody>
        </Card>
      </Col>

      {/* Discount Field */}
      <Col xl="12" sm="12" style={{ paddingLeft: "0px", marginTop: "15px", marginBottom: "15px" }}>
        <Card style={{ backgroundColor: "#f8f9fa", borderLeft: `4px solid ${discountedRecords ? "#28a745" : "#ffc107"}` }}>
          <CardBody style={{ padding: "10px 15px" }}>
            <Row>
              <Col md="12">
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "6px" }}>
                  <label htmlFor="discount" style={{ fontSize: "12px", fontWeight: "600", color: "#333", marginBottom: "0" }}>
                    Discount Percentage (%)
                  </label>
                  {discountedRecords && (
                    <Badge color="success" style={{ fontSize: "10px", padding: "4px 8px" }}>
                      ✓ Discount Applied
                    </Badge>
                  )}
                </div>
                <div style={{ display: "flex", gap: "8px", alignItems: "self-start" , justifyContent: "normal" }}>
                  <div >
                    <input
                      id="discount"
                    //   type="number"
                      placeholder="Enter discount %"
                      value={discountAmount}
                      onChange={(e) => setDiscountAmount(e.target.value)}
                      min="0"
                      max="100"
                      style={{
                        width: "100%",
                        padding: "6px 10px",
                        border: "1px solid #ddd",
                        borderRadius: "4px",
                        fontSize: "12px",
                        fontFamily: "'Poppins', sans-serif",
                        backgroundColor: "#fff",
                        cursor: "text",
                      }}
                    />
                    <small style={{ color: "#666", marginTop: "3px", display: "block", fontSize: "10px" }}>
                      {discountedRecords
                        ? `Discount of ${discountAmount}% applied to all records`
                        : "Type a % value — discount calculates automatically"
                      }
                    </small>
                  </div>
                  {discountedRecords && (
                    <Button
                      color="success"
                      onClick={handleRemoveDiscount}
                      style={{ padding: "6px 16px", fontSize: "11px", whiteSpace: "nowrap", alignSelf: "flex-start" }}
                    >
                      <i className="fa fa-times" style={{ marginRight: "4px",  }}></i>
                      Remove
                    </Button>
                  )}
                </div>
              </Col>
            </Row>
          </CardBody>
        </Card>
      </Col>

      {/* Action Buttons */}
      <Col xl="12" sm="12" style={{ paddingLeft: "0px", marginTop: "15px" }}>
        {/* Error Warning - Show when errorCount > 0 */}
        {errorCount > 0 && (
          <div style={{
            backgroundColor: "#fff3cd",
            border: "1px solid #ffc107",
            borderRadius: "8px",
            padding: "12px 15px",
            marginBottom: "15px",
            display: "flex",
            alignItems: "center",
            gap: "10px"
          }}>
            <i className="fa fa-exclamation-triangle" style={{ fontSize: "20px", color: "#856404" }}></i>
            <div style={{ flex: 1 }}>
              <strong style={{ color: "#856404", fontSize: "13px" }}>
                Cannot proceed: {errorCount} document{errorCount > 1 ? 's have' : ' has'} failed validation
              </strong>
              <p style={{ margin: "5px 0 0 0", fontSize: "12px", color: "#856404" }}>
                Download the error file, fix the issues, and upload a corrected file with 0 errors.
              </p>
            </div>
          </div>
        )}
        
        <div className="d-flex justify-content-between" style={{ gap: "10px" }}>
          <Button
            color="warning"
            className="rounded-pill"
            onClick={() => {
              // Show confirmation dialog before going back
              SweetAlert.fire({
                title: "Discard Changes?",
                html: `
                  <div style="text-align: left; font-family: 'Poppins', sans-serif;">
                    <p style="margin: 10px 0; font-size: 14px;">
                      Your discount data and file upload for this document will be lost. 
                    </p>
                    <p style="margin: 10px 0; font-size: 14px;">
                      <strong>Are you sure you want to proceed?</strong>
                    </p>
                  </div>
                `,
                icon: "warning",
                confirmButtonText: "Yes, Discard",
                cancelButtonText: "No, Keep Data",
                confirmButtonColor: "#dc3545",
                cancelButtonColor: "#00239c",
                reverseButtons: true,
                showCancelButton: true,
              }).then((result) => {
                if (result.isConfirmed) {
                  // Clear discount data and file data from Redux
                  dispatch(clearDiscountData());
                  // Call original back handler
                  handleBackClick();
                }
              });
            }}
            style={{ padding: "6px 20px", fontSize: "12px" }}
          >
            <span className="mr-2">{"<"}</span> Back
          </Button>
          <Button
            color={errorCount > 0 ? "secondary" : "primary"}
            className="rounded-pill"
            onClick={() => {
              // ✅ VALIDATION: Block if any documents failed
              if (errorCount > 0) {
                SweetAlert.fire({
                  title: "Cannot Proceed",
                  html: `
                    <div style="text-align: left; font-family: 'Poppins', sans-serif;">
                     
                      ${errorFile ? `
                        <div style="margin-top: 15px; text-align: center;">
                          <button 
                            id="downloadErrorFileBtnModal" 
                            style="padding: 8px 16px; background-color: #dc3545; color: white; border: none; border-radius: 4px; cursor: pointer; font-size: 13px;"
                            onclick="window.open('${errorFile}', '_blank')"
                          >
                            <i class="fa fa-download" style="margin-right: 6px;"></i> Download Error File
                          </button>
                        </div>
                      ` : ''}
                    </div>
                  `,
                  icon: "error",
                  confirmButtonText: "OK, I'll Fix the Errors",
                  confirmButtonColor: "#00239c",
                  allowOutsideClick: false,
                  allowEscapeKey: false,
                });
                return;
              }

              // ✅ VALIDATION: Ensure at least one successful record
              if (successCount === 0) {
                toast.error("No successful records found. Please upload a valid file.");
                return;
              }

              // All validations passed - proceed with saving
              saveQuotationForBulk();
            }}
            disabled={isSavingQuotation}
            style={{ 
              padding: "6px 20px", 
              fontSize: "12px",
              opacity: errorCount > 0 ? 0.6 : 1,
              cursor: errorCount > 0 ? "not-allowed" : "pointer"
            }}
            title={errorCount > 0 ? `Cannot proceed: ${errorCount} failed document${errorCount > 1 ? 's' : ''}. Fix errors first.` : "Proceed to next step"}
          >
            {isSavingQuotation ? (
              <>
                <span className="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true"></span>
                Saving...
              </>
            ) : (
              <>
                {errorCount > 0 && <i className="fa fa-lock" style={{ marginRight: "6px" }}></i>}
                Next <span className="ml-2">{">"}</span>
              </>
            )}
          </Button>
        </div>
      </Col>
      {/* Record Details Modal with Addon Dropdowns */}
      {detailsModalItem && (
        <RecordDetailsModal
          isOpen={detailsModalOpen}
          toggle={toggleDetailsModal}
          item={detailsModalItem}
          recordWithDiscount={detailsModalDiscountedRecord}
          dataSource={dataSource}
          handleAddonSelect={handleAddonSelect}
          removeAddon={removeAddon}
          calculateYears={calculateYears}
          selectedServices={selectedServices}
          additionalCost={additionalCost}
        />
      )}
    </Row>
  );
};

export default BulkDocumentSuccess;
